import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BookOpen, ExternalLink, CheckCircle, GraduationCap, FileText, Beaker, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const ResearchPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Research & University Projects | CTC Waterworks"
        description="CTC Waterworks equipment in university and extension research focused on irrigation water quality, soil pH, and high-value crops."
        canonicalPath="/research"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-50 via-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <GraduationCap size={16} />
              <span>University Research & Extension Work</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Research Library
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              CTC Waterworks equipment has been used in university and extension research focused on irrigation water quality, soil pH, and high-value crops.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">

          {/* Research Cards - NEW LAYOUT */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">

            {/* Texas A&M Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border-2 border-blue-300 shadow-lg h-full flex flex-col">
                <div className="flex items-start space-x-3 mb-6">
                  <FileText className="text-blue-600 flex-shrink-0 mt-1" size={32} />
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Texas A&M AgriLife
                    </h2>
                    <p className="text-blue-700 font-medium">Far West Texas Cotton</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  In calcareous soil regions with salinity and sodicity challenges, acidified irrigation water was tested as part of a comprehensive management program that included realistic leaching and drainage planning.
                </p>

                <div className="bg-white rounded-lg p-5 mb-6 shadow-md">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Findings</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700 text-sm">Salinity reduction in upper layer after one season</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700 text-sm">Modest SAR shifts by depth—distribution matters</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700 text-sm">~20% yield vs long-term average + fiber quality</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4 mb-6">
                  <p className="text-gray-800 text-sm font-medium">
                    One year; multi-year needed
                  </p>
                </div>

                <div className="mt-auto space-y-3">
                  <Link
                    to="/blog/texas-a-m-agrilife-cotton-study-sulfur-burner-treated-irrigation-reduced-root-zone-salinity-and-increased-yield-20"
                    className="block bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-5 py-3 rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 shadow-md text-center font-semibold"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>CTC breakdown</span>
                      <ArrowRight size={16} />
                    </div>
                  </Link>
                  <a
                    href="https://agrilifetoday.tamu.edu/2024/07/10/sulfur-burner-helps-improve-cotton-production-in-far-west-texas/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block border-2 border-blue-600 text-blue-600 px-5 py-3 rounded-lg hover:bg-blue-600 hover:text-white transition-all duration-300 text-center font-semibold"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>Texas A&M source</span>
                      <ExternalLink size={16} />
                    </div>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* OSU Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-300 shadow-lg h-full flex flex-col">
                <div className="flex items-start space-x-3 mb-6">
                  <Beaker className="text-green-600 flex-shrink-0 mt-1" size={32} />
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Oregon State University
                    </h2>
                    <p className="text-green-700 font-medium">Hermiston Organic Blueberries / Calcareous Soil</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  Researchers established an organic northern highbush blueberry planting on sandy, calcareous soil with high-pH irrigation water to study soil pH management and organic input strategies.
                </p>

                <div className="bg-white rounded-lg p-5 mb-6 shadow-md">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">What It Means for Commercial Growers</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700 text-sm">Pre-acidified soil + sulfur burner acidified irrigation</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700 text-sm">Maintained blueberry pH sweet spot in high-pH environment</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700 text-sm">Tested compost, biochar, and organic soil health strategies</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-100 border-l-4 border-green-500 rounded-lg p-4 mb-6">
                  <p className="text-gray-800 text-sm font-medium">
                    Real-world context for pH-sensitive, high-value crops
                  </p>
                </div>

                <div className="mt-auto space-y-3">
                  <Link
                    to="/blog/blueberries-high-ph-water-and-osu-s-organic-trial-what-acidified-irrigation-really-buys-you"
                    className="block bg-gradient-to-r from-green-600 to-emerald-600 text-white px-5 py-3 rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-md text-center font-semibold"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>Read article on our blog</span>
                      <ArrowRight size={16} />
                    </div>
                  </Link>
                  <div className="space-y-2">
                    <a
                      href="https://www.nature.com/articles/s41598-025-05761-z"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block border-2 border-green-600 text-green-600 px-5 py-3 rounded-lg hover:bg-green-600 hover:text-white transition-all duration-300 text-center font-semibold text-sm"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <span>Scientific Reports</span>
                        <ExternalLink size={14} />
                      </div>
                    </a>
                    <a
                      href="https://eorganic.org/node/35679"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block border-2 border-green-600 text-green-600 px-5 py-3 rounded-lg hover:bg-green-600 hover:text-white transition-all duration-300 text-center font-semibold text-sm"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <span>eOrganic webinar</span>
                        <ExternalLink size={14} />
                      </div>
                    </a>
                    <a
                      href="https://horticulture.oregonstate.edu/article/optimizing-soil-ph-organic-blueberries-balancing-act"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block border-2 border-green-600 text-green-600 px-5 py-3 rounded-lg hover:bg-green-600 hover:text-white transition-all duration-300 text-center font-semibold text-sm"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <span>OSU Extension article</span>
                        <ExternalLink size={14} />
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>

          {/* How to Read Research - NEW */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-8 border border-gray-200">
              <div className="flex items-start space-x-4 mb-4">
                <BookOpen className="text-gray-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    How to Read Research
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Research is context-dependent. What works in one trial reflects specific water chemistry, soil conditions, crop requirements, and management practices. Outcomes depend on operating constraints that may or may not match your situation.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    CTC helps evaluate fit using water and soil data. We look at bicarbonate levels, pH, salinity, sodicity indicators, drainage reality, and irrigation method to determine whether acidified irrigation makes sense for your operation.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    These research examples show what's possible when conditions align—not what's guaranteed everywhere.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Internal Links - NEW */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border border-blue-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Learn More</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Link
                  to="/sulfur-burners"
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-blue-200 hover:border-blue-400"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Sulfur Burners</h4>
                  <p className="text-gray-600 text-sm mb-4">Agricultural pH management systems for water treatment</p>
                  <div className="flex items-center text-blue-600 font-medium">
                    <span>View products</span>
                    <ArrowRight size={16} className="ml-2" />
                  </div>
                </Link>

                <Link
                  to="/sulfur-burner-guide"
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-blue-200 hover:border-blue-400"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Sulfur Burner Guide</h4>
                  <p className="text-gray-600 text-sm mb-4">Comprehensive guide to pH management and sustainable agriculture</p>
                  <div className="flex items-center text-blue-600 font-medium">
                    <span>Read guide</span>
                    <ArrowRight size={16} className="ml-2" />
                  </div>
                </Link>

                <Link
                  to="/blog/texas-a-m-agrilife-cotton-study-sulfur-burner-treated-irrigation-reduced-root-zone-salinity-and-increased-yield-20"
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-blue-200 hover:border-blue-400"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Texas A&M Article</h4>
                  <p className="text-gray-600 text-sm mb-4">Cotton study results: salinity reduction and yield increase</p>
                  <div className="flex items-center text-blue-600 font-medium">
                    <span>Read article</span>
                    <ArrowRight size={16} className="ml-2" />
                  </div>
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Educational Note */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-xl p-8 border border-blue-200"
          >
            <p className="text-sm text-gray-600 italic leading-relaxed">
              These resources are provided for educational purposes. CTC Waterworks is not speaking on behalf of Oregon State University or Texas A&M AgriLife, but we align our product design and recommendations with the kinds of water and pH challenges highlighted in this research.
            </p>
          </motion.div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResearchPage;
